import React, { useState, useEffect } from "react";
import { Button, DatePicker, Select, Input, Table, Row, Col, Tag, message } from "antd";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { isValid, isValidArray } from "../../utils/utilities";
import { getAPIDateFormat, getLocalDateAndTime } from "../../utils/date.utils";
import { loadingAction } from "../../actions/loading-actions";
import { Printer } from "../custom-components/custom-component";
import * as XLSX from 'xlsx';
import { getOriginCities, fetchSchedules } from "../../actions/home.action";
import { originsSelector } from "../../selector/home.selector";
import { getVechicles } from "../edit-vechicle-component/edit-vechicle.module";
import { getStaffDetails } from "../search-components/staff-select-component/staff-select.module";
import { StaffSelect } from "../search-components/staff-select-component/staff-select.component";
import {
    bulkUpdateTripVehicleInfo,
    mapSchedulesToRows
} from "./bulk-trip-vehicle-update.module";
import "./bulk-trip-vehicle-update.style.scss";

const { Option } = Select;

const ALL = "All";

/*
    Build vehicle <Option>s for a row, restricted to vehicles that belong to the
    row's schedule bus (same logic as the single-trip "Assign Vehicle & Driver").
*/
const renderVehicleOptions = (vehicles, busId) => {
    let options = [<Option value={0} key="none">None</Option>];
    if (isValidArray(vehicles)) {
        vehicles.forEach((vehicle) => {
            if (busId === vehicle.bus_id) {
                options.push(<Option value={vehicle.id} key={vehicle.id}>{vehicle.vechicle_no}</Option>);
            }
        });
    }
    return options;
};

const renderCityOptions = (cities) => {
    let options = [<Option value={ALL} key="all">All</Option>];
    if (isValidArray(cities)) {
        cities.forEach((city) => {
            if (isValid(city) && isValid(city.city_name)) {
                options.push(<Option value={city.city_name} key={city.city_name}>{city.city_name}</Option>);
            }
        });
    }
    return options;
};

const generateJsonToExport = (filters, rows, getVehicleNo) => {

    let reportsXmlData = [];
    if (isValidArray(rows) && rows.length > 0) {
        rows.forEach((row) => {
            reportsXmlData.push([
                row.description,
                row.origin,
                row.destination,
                isValid(row.start_time) ? row.start_time : '',
                getVehicleNo(row.vechicle_id) || '',
                row.driver_name || '',
                row.phone_no || '',
                row.second_driver_name || '',
                row.second_driver_phone_no || '',
                row.conductor_name || '',
                row.conductor_phone || '',
                row.trip_instruction || ''
            ]);
        });
    }

    let generatedTime = getLocalDateAndTime(new Date());

    var worksheet = XLSX.utils.aoa_to_sheet([
        [], // A1
        [], // A2
        ["Report", "Date", "From", "To", "Generated day/time"],
        ["Bulk Trip Vehicle Update", filters.date, filters.origin, filters.destination, generatedTime],
        [],
        [],
        [
            "Schedule", "Origin", "Destination", "Start Time", "Vehicle #",
            "Driver Name", "Driver #", "Driver Name 2", "Driver # 2",
            "Conductor Name", "Conductor #", "Trip Instruction"
        ],
        ...reportsXmlData
    ]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bulk Trip Vehicle Update');
    XLSX.writeFile(workbook, 'bulk-trip-vehicle-update.xlsx');
};

export const BulkTripVehicleUpdateComponent = () => {

    const dispatch = useDispatch();

    // Reuse the search page's origin cities (populated via getOriginCities).
    const cities = useSelector(originsSelector);

    const [date, setDate] = useState(moment());
    const [origin, setOrigin] = useState(ALL);
    const [destination, setDestination] = useState(ALL);

    const [vehicles, setVehicles] = useState([]);
    const [staff, setStaff] = useState([]);

    const [rows, setRows] = useState([]);
    const [dirtyIds, setDirtyIds] = useState({});
    const [searched, setSearched] = useState(false);

    useEffect(() => {
        dispatch(getOriginCities());
        getVechicles((data) => setVehicles(data));
        getStaffDetails((data) => setStaff(data), () => { });
    }, []);

    const setLoading = (value) => {
        dispatch(loadingAction(value));
    };

    const markDirty = (id) => {
        setDirtyIds((prev) => ({ ...prev, [id]: true }));
    };

    const updateRow = (id, changes) => {
        setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...changes } : row)));
        markDirty(id);
    };

    const onSearch = () => {
        if (!isValid(date)) {
            message.error("Please select a date");
            return;
        }
        // Reuse the search page's fetchSchedules; "all" (handled by sp_search_routes)
        // returns All-to-All. Loading + errors are handled inside the action's saga.
        dispatch(fetchSchedules(
            origin === ALL ? 'all' : origin,
            destination === ALL ? 'all' : destination,
            getAPIDateFormat(date),
            false,
            true,
            (schedules) => {
                setRows(mapSchedulesToRows(schedules));
                setDirtyIds({});
                setSearched(true);
            }
        ));
    };

    const getVehicleNoFromId = (vehicleId) => {
        let vehicleNo = null;
        if (isValidArray(vehicles)) {
            vehicles.forEach((vehicle) => {
                if (vehicleId == vehicle.id) {
                    vehicleNo = vehicle.vechicle_no;
                }
            });
        }
        return vehicleNo;
    };

    const buildTripPayload = (row) => {
        let vehicleId = row.vechicle_id == 0 ? null : row.vechicle_id;
        return {
            id: row.id,
            schedule_id: row.schedule_id,
            vechicle_id: vehicleId,
            vehicleNo: getVehicleNoFromId(vehicleId),
            phone_no: row.phone_no,
            driver_name: row.driver_name,
            second_driver_name: row.second_driver_name,
            second_driver_phone_no: row.second_driver_phone_no,
            conductor_name: row.conductor_name,
            conductor_phone: row.conductor_phone,
            operator_code: global.operatorCode,
            doj: getAPIDateFormat(row.start_date),
            trip_instruction: row.trip_instruction
        };
    };

    const onSubmit = () => {
        let changedRows = rows.filter((row) => dirtyIds[row.id]);
        if (changedRows.length === 0) {
            message.warning("No changes to update");
            return;
        }
        let trips = changedRows.map(buildTripPayload);
        setLoading(true);
        bulkUpdateTripVehicleInfo(
            trips,
            () => {
                setLoading(false);
                onSearch();
            },
            () => {
                setLoading(false);
            }
        );
    };

    const dirtyCount = Object.keys(dirtyIds).length;

    const columns = [
        {
            title: "Schedule",
            dataIndex: "description",
            key: "description",
            fixed: "left",
            width: 240,
            render: (text, row) => (
                <div>
                    <div className="bulk-vehicle-schedule-desc">{text}</div>
                    <div className="bulk-vehicle-schedule-route">
                        {row.origin} &rarr; {row.destination}
                        {isValid(row.start_time) ? ` | ${row.start_time}` : ''}
                    </div>
                    {dirtyIds[row.id] && <Tag color="orange">Modified</Tag>}
                </div>
            )
        },
        {
            title: "Vehicle #",
            dataIndex: "vechicle_id",
            key: "vechicle_id",
            width: 180,
            render: (value, row) => (
                <Select
                    showSearch
                    allowClear
                    placeholder="Select vehicle"
                    optionFilterProp="children"
                    value={isValid(value) ? value : undefined}
                    style={{ width: "100%" }}
                    filterOption={(input, option) =>
                        (option.children || "").toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(vehicleId) => updateRow(row.id, { vechicle_id: isValid(vehicleId) ? vehicleId : null })}
                >
                    {renderVehicleOptions(vehicles, row.bus_id)}
                </Select>
            )
        },
        {
            title: "Driver Name",
            dataIndex: "driver_name",
            key: "driver_name",
            width: 200,
            render: (value, row) => (
                <StaffSelect
                    data={staff}
                    isFormItem={false}
                    staffType="Driver"
                    selectStaff={value}
                    onChange={(name, option) => {
                        let changes = { driver_name: isValid(name) ? name : null };
                        if (isValid(option) && isValid(option.staff) && isValid(option.staff.phone_number)) {
                            changes.phone_no = option.staff.phone_number;
                        }
                        updateRow(row.id, changes);
                    }}
                />
            )
        },
        {
            title: "Driver #",
            dataIndex: "phone_no",
            key: "phone_no",
            width: 150,
            render: (value, row) => (
                <Input
                    value={value}
                    placeholder="Driver phone"
                    onChange={(e) => updateRow(row.id, { phone_no: e.target.value })}
                />
            )
        },
        {
            title: "Driver Name 2",
            dataIndex: "second_driver_name",
            key: "second_driver_name",
            width: 200,
            render: (value, row) => (
                <StaffSelect
                    data={staff}
                    isFormItem={false}
                    staffType="Driver"
                    selectStaff={value}
                    onChange={(name, option) => {
                        let changes = { second_driver_name: isValid(name) ? name : null };
                        if (isValid(option) && isValid(option.staff) && isValid(option.staff.phone_number)) {
                            changes.second_driver_phone_no = option.staff.phone_number;
                        }
                        updateRow(row.id, changes);
                    }}
                />
            )
        },
        {
            title: "Driver # 2",
            dataIndex: "second_driver_phone_no",
            key: "second_driver_phone_no",
            width: 150,
            render: (value, row) => (
                <Input
                    value={value}
                    placeholder="Driver 2 phone"
                    onChange={(e) => updateRow(row.id, { second_driver_phone_no: e.target.value })}
                />
            )
        },
        {
            title: "Conductor Name",
            dataIndex: "conductor_name",
            key: "conductor_name",
            width: 200,
            render: (value, row) => (
                <StaffSelect
                    data={staff}
                    isFormItem={false}
                    staffType="Conductor"
                    selectStaff={value}
                    onChange={(name, option) => {
                        let changes = { conductor_name: isValid(name) ? name : null };
                        if (isValid(option) && isValid(option.staff) && isValid(option.staff.phone_number)) {
                            changes.conductor_phone = option.staff.phone_number;
                        }
                        updateRow(row.id, changes);
                    }}
                />
            )
        },
        {
            title: "Conductor #",
            dataIndex: "conductor_phone",
            key: "conductor_phone",
            width: 150,
            render: (value, row) => (
                <Input
                    value={value}
                    placeholder="Conductor phone"
                    onChange={(e) => updateRow(row.id, { conductor_phone: e.target.value })}
                />
            )
        },
        {
            title: "Trip Instruction",
            dataIndex: "trip_instruction",
            key: "trip_instruction",
            width: 220,
            render: (value, row) => (
                <Input.TextArea
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    value={value}
                    placeholder="Trip instruction"
                    onChange={(e) => updateRow(row.id, { trip_instruction: e.target.value })}
                />
            )
        }
    ];

    // Read-only columns for the print view (no inputs/selects).
    const printColumns = [
        {
            title: "Schedule",
            dataIndex: "description",
            key: "description",
            render: (text, row) => (
                <div>
                    <div className="bulk-vehicle-schedule-desc">{text}</div>
                    <div className="bulk-vehicle-schedule-route">
                        {row.origin} &rarr; {row.destination}
                        {isValid(row.start_time) ? ` | ${row.start_time}` : ''}
                    </div>
                </div>
            )
        },
        {
            title: "Vehicle #",
            dataIndex: "vechicle_id",
            key: "vechicle_id",
            render: (value) => getVehicleNoFromId(value)
        },
        {
            title: "Driver Name",
            dataIndex: "driver_name",
            key: "driver_name"
        },
        {
            title: "Driver #",
            dataIndex: "phone_no",
            key: "phone_no"
        },
        {
            title: "Driver Name 2",
            dataIndex: "second_driver_name",
            key: "second_driver_name"
        },
        {
            title: "Driver # 2",
            dataIndex: "second_driver_phone_no",
            key: "second_driver_phone_no"
        },
        {
            title: "Conductor Name",
            dataIndex: "conductor_name",
            key: "conductor_name"
        },
        {
            title: "Conductor #",
            dataIndex: "conductor_phone",
            key: "conductor_phone"
        },
        {
            title: "Trip Instruction",
            dataIndex: "trip_instruction",
            key: "trip_instruction"
        }
    ];

    const onExport = () => {
        if (rows.length === 0) {
            message.warning("No trips to export");
            return;
        }
        generateJsonToExport(
            {
                date: getAPIDateFormat(date),
                origin: origin,
                destination: destination
            },
            rows,
            getVehicleNoFromId
        );
    };

    return (
        <div className="bulk-trip-vehicle-update-root">
            <Row>
                <h2>
                    <strong>Bulk Trip Vehicle Update</strong>
                </h2>
            </Row>
            <Row className="bulk-vehicle-search-bar" gutter={[16, 16]} align="bottom">
                <Col>
                    <div className="bulk-vehicle-field-label">Date</div>
                    <DatePicker
                        allowClear={false}
                        format="DD-MM-YYYY"
                        value={date}
                        onChange={(value) => setDate(value)}
                    />
                </Col>
                <Col>
                    <div className="bulk-vehicle-field-label">From</div>
                    <Select
                        showSearch
                        value={origin}
                        style={{ minWidth: 180 }}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option.children || "").toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        onChange={(value) => setOrigin(value)}
                    >
                        {renderCityOptions(cities)}
                    </Select>
                </Col>
                <Col>
                    <div className="bulk-vehicle-field-label">To</div>
                    <Select
                        showSearch
                        value={destination}
                        style={{ minWidth: 180 }}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option.children || "").toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        onChange={(value) => setDestination(value)}
                    >
                        {renderCityOptions(cities)}
                    </Select>
                </Col>
                <Col>
                    <Button type="primary" onClick={onSearch}>Search</Button>
                </Col>
            </Row>

            {searched &&
                <Row className="bulk-vehicle-action-bar" justify="space-between" align="middle">
                    <Col>
                        <span className="bulk-vehicle-count">{rows.length} trip(s)</span>
                        {dirtyCount > 0 && <Tag color="orange">{dirtyCount} unsaved change(s)</Tag>}
                    </Col>
                    <Col>
                        <Row gutter={[8, 8]} align="middle">
                            <Col>
                                <Button onClick={onExport}>Export</Button>
                            </Col>
                            <Col>
                                <Printer
                                    showComponent={false}
                                    zoomValue={0.6}
                                    renderComponent={() => (
                                        <div>
                                            <Row>
                                                <h3>
                                                    <strong>Bulk Trip Vehicle Update</strong>
                                                    {` | ${getAPIDateFormat(date)} | ${origin} → ${destination}`}
                                                </h3>
                                            </Row>
                                            <Table
                                                dataSource={rows}
                                                columns={printColumns}
                                                rowKey={(row) => row.id}
                                                pagination={false}
                                            />
                                        </div>
                                    )}
                                />
                            </Col>
                            <Col>
                                <Button
                                    type="primary"
                                    disabled={dirtyCount === 0}
                                    onClick={onSubmit}
                                >
                                    Submit
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            }

            <Table
                className="bulk-vehicle-table"
                dataSource={rows}
                columns={columns}
                rowKey={(row) => row.id}
                pagination={false}
                scroll={{ x: "max-content" }}
                locale={{ emptyText: searched ? "No trips found for the selected filters" : "Search to load trips" }}
            />
        </div>
    );
};
